// D36 rep-prescription presentation helpers (Phase 7A).
//
// The canonical modes are DERIVED, never stored as an enum:
//
//   fixed reps  targetReps = N    minReps/maxReps/targetSeconds null
//   rep range   minReps = MIN     maxReps = MAX   targetReps/targetSeconds null
//   time        targetSeconds = S targetReps/minReps/maxReps null
//   no target   all four null
//
// This module only READS the builder's own set shape to decide what the control
// should show. It writes nothing: every edit goes through the Phase 6A mutators
// (`updateSet(..., { repTarget: { mode, ... } }, exerciseIndex)`), which own the
// atomic four-field write contract.
//
// Ceilings mirror `backend/services/repPrescription.js` and the identical
// constants in both program contexts. The server stays authoritative; these
// exist so the control can tell "the user changed something" from "the user
// re-entered the value that is already persisted" without emitting a no-op
// mutation.
export const MAX_REP_TARGET = 100;
export const MAX_TARGET_SECONDS = 600;

export function resolveMeasurementType(value) {
  return value?.executionProfile?.measurementType ||
    value?.measurementType ||
    "REPS";
}

export function isTimedHold(value) {
  return resolveMeasurementType(value) === "TIMED_HOLD";
}

export const REP_MODES = Object.freeze({
  fixed: "reps",
  range: "repRange",
  time: "seconds",
});

const KNOWN_REP_MODES = new Set(Object.values(REP_MODES));

// The two visible strength fields. The mode is INFERRED from which of them the
// user has filled -- there is no mode selector any more:
//
//   left empty                 -> no target
//   left only                  -> fixed reps
//   left + right               -> rep range (left = min, right = max)
//
// `primary` is the fixed value or the range minimum, `secondary` the range
// maximum. `ghost` is what the (empty) right field suggests while the set is
// still fixed -- presentation only, never a value, never written.
export function resolveStrengthRepFields(prescription) {
  const isRange = prescription.mode === REP_MODES.range;
  const primary = isRange ? prescription.minReps : toRepInt(prescription.reps);
  const secondary = isRange ? prescription.maxReps : null;

  return {
    isRange,
    primary,
    secondary,
    // The right field only exists once the left one holds a usable value.
    hasSecondaryField: primary != null,
    ghost: isRange ? null : primary,
    // A REPS exercise cannot author time. Its legacy persisted seconds value
    // stays visible so it is never silently invisible or rewritten.
    legacySeconds:
      prescription.mode === REP_MODES.time ? prescription.targetSeconds : null,
  };
}

export function toRepInt(value) {
  if (value == null || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
}

export function clampRepTargetValue(value, maximum) {
  const parsed = toRepInt(value);

  if (parsed == null) {
    return null;
  }

  return Math.min(maximum, Math.max(1, parsed));
}

function hasOwn(value, key) {
  return Boolean(value) && Object.prototype.hasOwnProperty.call(value, key);
}

// Cycle hydrates the additive `display` read model on every builder set, so its
// backend-computed `repMode` is the canonical source there. Weekly hydrates the
// explicit `minReps` / `maxReps` / `targetSeconds` columns instead. A LOCAL edit
// always wins over both, because `editIntent.repTarget.mode` is exactly the mode
// the Phase 6A mutator just wrote.
export function resolveSetRepPrescription(set, measurementType = "REPS") {
  const display = set?.display || null;
  const intentMode = set?.editIntent?.repTarget?.mode;

  const targetSeconds = hasOwn(set, "targetSeconds")
    ? toRepInt(set.targetSeconds)
    : toRepInt(display?.targetSeconds);
  const minReps = hasOwn(set, "minReps")
    ? toRepInt(set.minReps)
    : toRepInt(display?.minReps);
  const maxReps = hasOwn(set, "maxReps")
    ? toRepInt(set.maxReps)
    : toRepInt(display?.maxReps);
  const reps = set?.reps;
  const persistedTargetReps = toRepInt(
    set?.persistence?.targetReps ?? display?.targetReps
  );

  if (measurementType === "TIMED_HOLD") {
    const legacyMinimum = toRepInt(
      set?.persistence?.minReps ?? display?.minReps
    );
    const legacyMaximum = toRepInt(
      set?.persistence?.maxReps ?? display?.maxReps
    );
    const legacyReps = targetSeconds != null
      ? null
      : persistedTargetReps != null
        ? `${persistedTargetReps} reps`
        : legacyMinimum != null || legacyMaximum != null
          ? `${legacyMinimum ?? "?"}-${legacyMaximum ?? "?"} reps`
          : null;

    return {
      mode: REP_MODES.time,
      reps: "",
      minReps: null,
      maxReps: null,
      targetSeconds,
      legacyReps,
    };
  }

  let mode;

  if (KNOWN_REP_MODES.has(intentMode)) {
    mode = intentMode;
  } else if (display && KNOWN_REP_MODES.has(display.repMode)) {
    mode = display.repMode;
  } else if (targetSeconds != null) {
    mode = REP_MODES.time;
  } else if (minReps == null && maxReps == null) {
    mode = REP_MODES.fixed;
  } else if (persistedTargetReps != null) {
    // Every user-authored fixed set written before Phase 6A serialized as the
    // `targetReps = minReps = maxReps = N` mirror, which the domain accepts as
    // backward-compatible fixed reps. Weekly has no `display` object, so the
    // persisted fixed target is the only unambiguous signal. This is the single
    // narrow read of `persistence`, it disambiguates a documented legacy shape,
    // and it never reconstructs anything else.
    mode = REP_MODES.fixed;
  } else {
    mode = REP_MODES.range;
  }

  return {
    mode,
    reps: reps == null || reps === "" ? "" : reps,
    minReps,
    maxReps,
    targetSeconds,
  };
}

export function formatRepPrescriptionSummary(prescription) {
  if (prescription.mode === REP_MODES.time) {
    return prescription.targetSeconds == null
      ? "No target"
      : `${prescription.targetSeconds} sec`;
  }

  if (prescription.mode === REP_MODES.range) {
    if (prescription.minReps == null && prescription.maxReps == null) {
      return "No target";
    }

    return `${prescription.minReps ?? "?"}-${prescription.maxReps ?? "?"} reps`;
  }

  const fixed = toRepInt(prescription.reps);
  return fixed == null ? "No target" : `${fixed} reps`;
}
